import { Link, createFileRoute } from '@tanstack/react-router';

import { NotFoundState } from '@/shared/components/NotFoundState';
import { useReducedMotion } from '@/shared/hooks/useReducedMotion';
import { BrowsePage, movedPreviewPath } from '@/features/workspace-files';
import { isInsideFolder } from '@/features/workspace-files/folders';
import { filesSearchSchema } from '@/features/workspace-files/searchParams';

export const Route = createFileRoute('/workspaces/$workspaceId/files/')({
  // Current folder, selected file, and which of its views is on screen are
  // shareable and survive a reload, so they live in the address rather than in
  // a store (FR-015/FR-F015). Sort key/direction stay component-local — not
  // part of this schema (research.md R10).
  validateSearch: filesSearchSchema,
  component: FilesRoute,
  notFoundComponent: FilesNotFound,
});

function FilesRoute() {
  const { workspaceId } = Route.useParams();
  const { path, file, view } = Route.useSearch();
  const navigate = Route.useNavigate();
  const reducedMotion = useReducedMotion();

  return (
    <BrowsePage
      workspaceId={workspaceId}
      path={path}
      selectedFile={file}
      view={view}
      onNavigate={(nextPath) =>
        void navigate({
          search: (prev) => ({
            ...prev,
            path: nextPath,
            file: undefined,
          }),
        })
      }
      // The name travels from the row into the preview's header. A view
      // transition is not a style rule, so the preference is asked here rather
      // than left to the stylesheet (spec 075 FR-031).
      onSelectFile={(nextFile) =>
        void navigate({
          search: (prev) => ({ ...prev, file: nextFile }),
          viewTransition: !reducedMotion,
        })
      }
      onViewChange={(nextView) =>
        void navigate({ search: (prev) => ({ ...prev, view: nextView }) })
      }
      onCloseFile={() =>
        void navigate({
          search: (prev) => ({ ...prev, file: undefined }),
        })
      }
      onFileMoved={(from, to) =>
        void navigate({
          search: (prev) => {
            // A file's identity here is its path, so the moment one moves,
            // anything still holding the old path is holding nothing. Only the
            // previewed file needs following — whether it moved itself or sits
            // inside a folder that did (FR-027, spec 103 FR-024); every other
            // move leaves the selection alone.
            if (prev.file === undefined) return prev;
            const followed = movedPreviewPath(prev.file, from, to);
            return followed === undefined ? prev : { ...prev, file: followed };
          },
        })
      }
      onFileDeleted={(deletedPath) =>
        void navigate({
          search: (prev) =>
            prev.file !== undefined &&
            (prev.file === deletedPath ||
              // A deleted folder takes everything under it, including whatever
              // was open in the preview.
              isInsideFolder(prev.file, deletedPath))
              ? { ...prev, file: undefined }
              : prev,
        })
      }
    />
  );
}

export function FilesNotFound() {
  const { workspaceId } = Route.useParams();
  return (
    <NotFoundState
      title="Not found"
      description="This folder or file does not exist in this workspace. It may have been renamed, moved, or deleted since the link was shared."
      // A dead end is not a recovery surface, which `NotFoundState` says of
      // itself and three of its four uses ignored. The top of the same
      // workspace's files is the nearest place that certainly exists.
      action={
        <Link
          to="/workspaces/$workspaceId/files"
          params={{ workspaceId }}
          search={{ path: '', view: 'preview' as const }}
          className="text-primary focus-visible:ring-ring rounded-sm text-sm hover:underline focus-visible:ring-3 focus-visible:outline-none"
        >
          Go to all files
        </Link>
      }
    />
  );
}
